import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  Notice,
  Badge,
  Breadcrumbs,
  Breadcrumb,
} from "@conuti-das/prince-ui";
import { Flex } from "@/ui-prince";
import { StateGlyph, toFlowState } from "@/components/flow";
import runsData from "@/data/generated/runs.json";
import type { Run, StageStatus } from "@/types";
import { getRun } from "@/api/runs";
import { StageActions } from "@/components/runs/StageActions";

const STAGE_LABELS: Record<string, string> = {
  scrape: "Scrape (BDEW)",
  mirror: "Mirror",
  parse: "Parse",
  generate_bpmn: "Generate BPMN",
  generate_dmn: "Generate DMN",
  generate_ticket: "Generate Tickets",
  process_repo: "Process-Repo Commit",
  camunda_deploy: "Camunda Deploy",
};

type BadgeTone = "neutral" | "green" | "red" | "orange" | "blue" | "teal" | "gray";

const STATE_TO_TONE: Record<string, BadgeTone> = {
  success: "green",
  running: "orange",
  failed: "red",
  pending: "neutral",
  skipped: "blue",
  planned: "blue",
  queued: "blue",
  cancelled: "red",
  awaiting_user: "orange",
};

export function RunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const runs = runsData as Run[];
  const staticRun = runs.find((r) => r.id === runId);
  const [run, setRun] = useState<Run | null>(staticRun ?? null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    async function refresh() {
      try {
        const r = await getRun(runId!);
        if (!cancelled) setRun(r);
      } catch {
        // runner offline → keep static
      }
    }
    refresh();
    const t = setInterval(refresh, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [runId, tick]);

  if (!run) {
    return (
      <div className="page">
        <Notice tone="negative">
          Run mit ID <code>{runId}</code> existiert nicht. <Link to="/activity">Zur Zeitachse</Link>
        </Notice>
      </div>
    );
  }

  const stages = Object.keys(STAGE_LABELS) as (keyof Run["stages"])[];

  return (
    <div className="page">
      <Breadcrumbs>
        <Breadcrumb><Link to="/activity" style={{ textDecoration: "none" }}>Aktivität</Link></Breadcrumb>
        <Breadcrumb><span>{run.id}</span></Breadcrumb>
      </Breadcrumbs>

      <div className="page-header" style={{ marginTop: "var(--s-3)" }}>
        <h2 className="page-title">Run {run.id}</h2>
        <div className="page-subtitle">
          Release <strong>{run.release}</strong>
          {" · "}
          {new Date(run.trigger_date).toLocaleString("de-DE")}
          {" · "}
          Trigger: <code>{run.triggered_by}</code>
        </div>
      </div>

      {run.description && (
        <div style={{ marginBottom: "var(--s-4)" }}>
          <Notice tone="info">
            {run.description}
          </Notice>
        </div>
      )}

      <div className="section-title">Pipeline-Status</div>
      <div className="stages-board">
        {stages.map((key) => {
          const stage = run.stages[key];
          const state = stage?.state ?? "pending";
          return (
            <StageCard
              key={key}
              title={STAGE_LABELS[key]}
              stage={stage}
              state={state}
              actions={
                <StageActions
                  runId={run.id}
                  stage={key}
                  state={state}
                  jobId={stage?.job_id}
                  onChanged={() => setTick((t) => t + 1)}
                />
              }
            />
          );
        })}
      </div>

      {run.artifacts && run.artifacts.length > 0 && (
        <div style={{ marginTop: "var(--s-5)" }}>
          <div className="section-title">Artefakte in diesem Run ({run.artifacts.length})</div>
          <Flex style={{ gap: "var(--s-1)", flexWrap: "wrap" }}>
            {run.artifacts.slice(0, 100).map((a) => (
              <Link key={a.id} to={`/artifacts/${a.type}/${run.release}`} style={{ textDecoration: "none" }}>
                <Badge tone="blue">{a.id}</Badge>
              </Link>
            ))}
          </Flex>
        </div>
      )}

      {run.tickets && run.tickets.length > 0 && (
        <div style={{ marginTop: "var(--s-5)" }}>
          <div className="section-title">Erzeugte Tickets ({run.tickets.length})</div>
          <div className="stack">
            {run.tickets.map((t) => (
              <Card key={t.id}>
                <Flex style={{ padding: "var(--s-3) var(--s-4)", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{t.id}</span>
                    {" — "}
                    <span>{t.title}</span>
                  </div>
                  <Badge tone={t.status === "done" ? "green" : "blue"}>
                    {t.status}
                  </Badge>
                </Flex>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StageCard({ title, stage, state, actions }: {
  title: string;
  stage?: StageStatus;
  state: string;
  actions?: React.ReactNode;
}) {
  const tone = STATE_TO_TONE[state] ?? "neutral";
  return (
    <Card
      title={
        <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
          {/* Dieselbe Zustands-Grammatik wie in der Runs-Kette — die
              hartkodierten `.stage-dot`-Punkte sind entfallen. */}
          <StateGlyph state={toFlowState(state)} size={14} labelled />
          <span>{title}</span>
        </Flex>
      }
      header={<Badge tone={tone}>{state}</Badge>}
    >
      <div style={{ padding: "var(--s-2) var(--s-4) var(--s-3)" }}>
        <Badge tone={tone} style={{ marginBottom: 6 }}>{state}</Badge>
        {stage?.started_at && <div style={{ fontSize: "0.78rem", color: "var(--sapContent_LabelColor)" }}>Start: {new Date(stage.started_at).toLocaleString("de-DE")}</div>}
        {stage?.completed_at && <div style={{ fontSize: "0.78rem", color: "var(--sapContent_LabelColor)" }}>Ende: {new Date(stage.completed_at).toLocaleString("de-DE")}</div>}
        {stage?.artifacts_count !== undefined && <div style={{ fontSize: "0.78rem", color: "var(--sapContent_LabelColor)" }}>Artefakte: {stage.artifacts_count}</div>}
        {stage?.error && <div style={{ fontSize: "0.78rem", color: "var(--sapNegativeColor)" }}>Fehler: {stage.error}</div>}
        {actions}
      </div>
    </Card>
  );
}
